import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../lib/supabase';
import { NAVY, GOLD, WHITE } from '../lib/ui';

const fmt = d => {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  if (diff < 60000)    return 'just now';
  if (diff < 3600000)  return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short' });
};

const TYPE_ICONS = {
  task_assigned: '📋',
  task_note:     '💬',
  message:       '✉️',
  deadline:      '⏰',
  matter:        '📁',
};

// Toast notification that appears briefly
function Toast({ notif, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 20, zIndex: 9999,
      background: NAVY, borderRadius: 12, padding: '14px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)', maxWidth: 320,
      border: `1px solid ${GOLD}44`, display: 'flex', gap: 10, alignItems: 'flex-start',
      animation: 'slideIn 0.3s ease',
    }}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(120%); opacity:0; } to { transform: translateX(0); opacity:1; } }
      `}</style>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{TYPE_ICONS[notif.type] || '🔔'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: GOLD, marginBottom: 3 }}>{notif.title}</div>
        {notif.body && <div style={{ fontSize: 12, color: '#8AACCC', lineHeight: 1.5 }}>{notif.body}</div>}
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6A7A9A', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
    </div>
  );
}

export default function NotificationBell({ recipientId, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [open,          setOpen]          = useState(false);
  const [toast,         setToast]         = useState(null);
  const panelRef = useRef();

  const unread = notifications.filter(n => !n.is_read).length;

  // Load notifications
  useEffect(() => {
    if (!recipientId) return;
    getNotifications(recipientId).then(data => setNotifications(data || []));
  }, [recipientId]);

  // Real-time subscription
  useEffect(() => {
    if (!recipientId) return;
    const sub = supabase
      .channel(`notifs:${recipientId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `recipient_id=eq.${recipientId}`
      }, payload => {
        const notif = payload.new;
        setNotifications(ns => [notif, ...ns]);
        setToast(notif); // Show toast popup
      })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [recipientId]);

  // Close panel when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      await markNotificationRead(notif.id);
      setNotifications(ns => ns.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    setOpen(false);
    if (notif.link_view && onNavigate) onNavigate(notif.link_view, notif.link_id);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead(recipientId);
    setNotifications(ns => ns.map(n => ({ ...n, is_read: true })));
  };

  return (
    <>
      {/* Bell button */}
      <div ref={panelRef} style={{ position: 'relative' }}>
        <button onClick={() => setOpen(o => !o)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          position: 'relative', padding: '6px 8px', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 20 }}>🔔</span>
          {unread > 0 && (
            <div style={{
              position: 'absolute', top: 2, right: 2,
              background: '#C0392B', color: WHITE, borderRadius: '50%',
              width: 17, height: 17, fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `2px solid ${WHITE}`,
            }}>{unread > 9 ? '9+' : unread}</div>
          )}
        </button>

        {/* Notification panel */}
        {open && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 8,
            width: 340, maxHeight: 480, background: WHITE, borderRadius: 12,
            border: '1px solid #E5E0D8', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 1000, overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #F0EDE8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: NAVY }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: GOLD, fontFamily: 'Georgia,serif' }}>
                Notifications {unread > 0 && <span style={{ background: '#C0392B', color: WHITE, borderRadius: 20, padding: '1px 7px', fontSize: 11, marginLeft: 6 }}>{unread}</span>}
              </span>
              {unread > 0 && (
                <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', color: '#6A7A9A', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {notifications.length === 0 && (
                <div style={{ padding: '32px 16px', textAlign: 'center', color: '#BBB', fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                  No notifications yet
                </div>
              )}
              {notifications.map(n => (
                <div key={n.id} onClick={() => handleClick(n)} style={{
                  padding: '12px 16px', borderBottom: '1px solid #F7F5F0',
                  cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                  background: n.is_read ? WHITE : `${GOLD}0A`,
                  transition: 'background 0.1s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F7F5F0'}
                  onMouseLeave={e => e.currentTarget.style.background = n.is_read ? WHITE : `${GOLD}0A`}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{TYPE_ICONS[n.type] || '🔔'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: n.is_read ? 400 : 700, color: NAVY, lineHeight: 1.4 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: 11, color: '#888', marginTop: 2, lineHeight: 1.5 }}>{n.body}</div>}
                    <div style={{ fontSize: 10, color: '#CCC', marginTop: 4 }}>{fmt(n.created_at)}</div>
                  </div>
                  {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: GOLD, flexShrink: 0, marginTop: 5 }} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toast popup */}
      {toast && <Toast notif={toast} onClose={() => setToast(null)} />}
    </>
  );
}
